import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { DesignToken, getDesignTokens } from '../base/core';
import Spacer, { spc } from '../base/spacer';
import Text from '../base/text';
import { useDashboardDataContext } from './dashboard/dashboard-context';

import Logger from '~/client/logger';
import {
  aiDashboardUtils,
  functionalModelsRouteUtil,
  candidateModelsDetailsRouteUtil,
  candidateAgentsDetailsRouteUtil,
  dashboardRouteUtil,
  orgAdminDetailsUtils,
  projectRouteUtil,
  settingsRoutesUtil,
} from '~/client/routes';
import * as GQL from '~/graphql/generated/client';

const logger = Logger('dynamic-breadcrumb');

interface BreadcrumbItem {
  id: string;
  name: string;
  isLast: boolean;
  navigateTo: () => void;
  icon?: React.ReactNode;
}

// Static dashboard page mappings
const DASHBOARD_PAGE_NAMES: Record<string, string> = {
  compliance: 'Compliance Dashboard',
  project: 'Project',
  organization: 'Organization',
  tasks: 'Tasks',
  risk: 'Risk',
  'policy-library': 'Policy Library',
  'assurance-library': 'Assurance Library',
  'vendor-model': 'Vendor Models',
  'vendor-agent': 'Vendor Agents',
  'test-gen': 'Test Generation',
  'control-gen': 'Control Generation',
  'policy-gen': 'Policy Generation',
  'create-ai-system': 'Create AI System',
  reports: 'Reports',
  'responsible-ai-training': 'Responsible AI Training',
};

// Map dashboard page URLs to route keys
const DASHBOARD_PAGE_TO_ROUTE: Record<string, string> = {
  compliance: 'COMPLIANCE',
  project: 'PROJECT',
  organization: 'ORGANIZATION',
  tasks: 'TASKS',
  risk: 'RISK',
  'policy-library': 'POLICY_LIBRARY',
  'assurance-library': 'ASSURANCE_LIBRARY',
  'vendor-model': 'VENDOR_MODEL',
  'vendor-agent': 'VENDOR_AGENT',
  'test-gen': 'TEST_GEN',
  'control-gen': 'CONTROL_GEN',
  'policy-gen': 'POLICY_GEN',
  'create-ai-system': 'CREATE_AI_SYSTEM',
  reports: 'REPORTS',
  'responsible-ai-training': 'RESPONSIBLE_AI_TRAINING',
};

// Project page mappings
const PROJECT_PAGE_NAMES: Record<string, string> = {
  dashboard: 'Dashboard',
  description: 'Description',
  'policy-details': 'Policy Details',
  assessments: 'Assessments',
  'assess-project': 'Assess Project',
  report: 'Report',
  'asenion-chatbot': 'AI Assistant',
};

// AI System page mappings
const AI_SYSTEM_PAGE_NAMES: Record<string, string> = {
  dashboard: 'Dashboard',
  'models-list': 'Models',
  'ai-usecases-list': 'AI Use Cases',
  'ai-usecase': 'AI Use Case',
  'create-model': 'Create Model',
  'create-ai-usecase': 'Create AI Use Case',
  'create-dataset': 'Create Dataset',
  'assessment-details': 'Assessment Details',
  'project-members': 'Stakeholders',
  'add-policy': 'Add Policy',
  'assess-project': 'Assess Project',
  'framework-analysis': 'Framework Analysis',
  'data-lineage-map': 'Data Lineage & Usage Map',
  'ai-system-report': 'AI System Report',
  report: 'Report',
  'advanced-reports': 'Risk Intelligence Report',
  'risk-assessment-report': 'Risk Assessment Report',
  'action-report': 'Action Report',
  'eu-ai-deep-dive-report': 'EU AI Deep Dive Report',
  'risk-reduction-report': 'Risk Reduction Report',
  monitoring: 'Monitoring',
};

// Governance page mappings
const GOVERNANCE_PAGE_NAMES: Record<string, string> = {
  'control-bundles': 'Control Bundles',
  policies: 'Policies',
  'policy-details': 'Policy Details',
};

// Settings page mappings
const SETTINGS_PAGE_NAMES: Record<string, string> = {
  'generate-token': 'API Tokens',
  'account-security': 'Account Security',
  'reset-password': 'Reset Password', // NOSONAR - This is a display label, not a hardcoded credential
  connections: 'Connections',
  configuration: 'Configuration',
  'system-admin': 'System Admin',
};

// Admin page mappings
const ADMIN_PAGE_NAMES: Record<string, string> = {
  dashboard: 'Dashboard',
  'system-admin': 'System Admin',
  'org-admin': 'Organization Admin',
};

// System Admin page mappings
const SYSTEM_ADMIN_PAGE_NAMES: Record<string, string> = {
  dashboard: 'Dashboard',
};

interface DynamicBreadcrumbProps {
  compact?: boolean;
  /** When true and compact, use nav bar colors (for use inside TopBar). */
  inTopBar?: boolean;
}

const DynamicBreadcrumb: React.FC<DynamicBreadcrumbProps> = ({
  compact = false,
  inTopBar = false,
}) => {
  const theme = useTheme();
  const [breadcrumbItems, setBreadcrumbItems] = useState<BreadcrumbItem[]>([]);
  const { projectId, functionalModelId, candidateId, agentId } = useParams();
  const location = useLocation();

  const aiSystem = aiDashboardUtils.useHelper();
  const functionalModel = functionalModelsRouteUtil.useHelper();
  const candidateModel = candidateModelsDetailsRouteUtil.useHelper();
  const candidateAgent = candidateAgentsDetailsRouteUtil.useHelper();
  const dashboard = dashboardRouteUtil.useHelper();
  const project = projectRouteUtil.useHelper();
  const settingsNav = settingsRoutesUtil.useHelper();
  const orgAdminNav = orgAdminDetailsUtils.useHelper();

  const [getProject] = GQL.useProjectFromIdLazyQuery();

  // Pull the outermost project name from the dashboard context. The breadcrumb
  // is rendered inside <DashboardDataProvider>, which already runs the Phase 1
  // query for the route's projectId — so we can render the real name on first
  // paint instead of fetching it again here.
  const {
    project: contextProject,
    projectName: contextProjectName,
    loading: dashboardLoading,
  } = useDashboardDataContext();
  // Guard against navigation races: the context may still hold the previous
  // project's data while a new route is loading. Only trust the name when it
  // belongs to the project currently in the URL.
  const contextProjectNameForRoute =
    contextProject?._id === projectId && contextProjectName
      ? contextProjectName
      : '';

  // Handle governance library type tracking based on referrer
  useEffect(() => {
    const referrer = document.referrer;
    if (referrer.includes('/dashboard/assurance-library')) {
      sessionStorage.setItem('governanceLibraryType', 'assurance');
    } else if (referrer.includes('/dashboard/policy-library')) {
      sessionStorage.setItem('governanceLibraryType', 'policy');
    }
  }, []);

  const buildStaticBreadcrumb = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const items: BreadcrumbItem[] = [];

    // Handle different route patterns
    if (pathSegments.length > 1) {
      const firstSegment = pathSegments[0];

      // Dashboard pages
      if (firstSegment === 'dashboard') {
        const dashboardPage = pathSegments[1];
        const pageName = DASHBOARD_PAGE_NAMES[dashboardPage];

        if (pageName) {
          // For compliance page, show "Compliance Dashboard" with home icon (no separate Dashboard breadcrumb)
          if (dashboardPage === 'compliance') {
            items.push({
              id: dashboardPage,
              name: pageName,
              isLast: pathSegments.length === 2 && !projectId,
              icon: <HomeIcon />,
              navigateTo: () => dashboard.navigateTo('COMPLIANCE'),
            });
          } else {
            // For other dashboard pages, add Compliance Dashboard first
            items.push({
              id: 'compliance',
              name: 'Compliance Dashboard',
              isLast: false,
              icon: <HomeIcon />,
              navigateTo: () => dashboard.navigateTo('COMPLIANCE'),
            });

            items.push({
              id: dashboardPage,
              name: pageName,
              isLast: pathSegments.length === 2 && !projectId,
              navigateTo: () => {
                const routeKey = DASHBOARD_PAGE_TO_ROUTE[dashboardPage];
                if (routeKey) {
                  dashboard.navigateTo(routeKey as any);
                }
              },
            });
          }
        }
      }
    } else {
      // Root dashboard path - show Compliance Dashboard
      items.push({
        id: 'compliance',
        name: 'Compliance Dashboard',
        isLast: true,
        icon: <HomeIcon />,
        navigateTo: () => dashboard.navigateTo('COMPLIANCE'),
      });
    }

    return items;
  };

  const buildProjectPageBreadcrumb = (
    items: BreadcrumbItem[],
    pathSegments: string[]
  ) => {
    // Handle project and ai-system routes
    const routeType = pathSegments[0]; // 'project' or 'ai-system'

    if (pathSegments.length > 2) {
      const projectPage = pathSegments[2];
      let pageName = '';

      if (routeType === 'project') {
        pageName = PROJECT_PAGE_NAMES[projectPage] || projectPage;
      } else if (routeType === 'ai-system') {
        pageName = AI_SYSTEM_PAGE_NAMES[projectPage] || projectPage;
      }

      if (pageName) {
        const isLastPage =
          pathSegments.length === 3 &&
          !functionalModelId &&
          !candidateId &&
          !agentId;

        items.push({
          id: projectPage,
          name: pageName,
          isLast: isLastPage,
          navigateTo: () => {
            if (routeType === 'project' && projectId) {
              // Navigate to project page
              project.navigateTo('DASHBOARD', {
                params: { projectId },
              });
            } else if (routeType === 'ai-system' && projectId) {
              // Map page segments to their corresponding AI system route keys
              const AI_SYSTEM_PAGE_ROUTE_MAP: Record<string, string> = {
                dashboard: 'DASHBOARD',
                'models-list': 'MODELS_LIST',
                'ai-usecases-list': 'FUNCTIONAL_MODELS_LIST',
                'ai-usecase': 'FUNCTIONAL_MODELS_LIST',
                'create-model': 'CREATE_MODEL',
                'create-ai-usecase': 'CREATE_FUNCTIONAL_MODEL',
                'create-dataset': 'CREATE_DATASET',
                'assessment-details': 'ASSESSMENT_DETAILS',
                'project-members': 'PROJECT_MEMBERS',
                'add-policy': 'ADD_PROJECT_POLICY',
                'assess-project': 'ASSESS_PROJECT',
                report: 'REPORT',
                'ai-system-report': 'AI_SYSTEM_REPORT',
                'framework-analysis': 'FRAMEWORK_ANALYSIS',
                'data-lineage-map': 'DATA_LINEAGE_MAP',
              };
              const routeKey = AI_SYSTEM_PAGE_ROUTE_MAP[projectPage];
              aiSystem.navigateTo((routeKey || 'DASHBOARD') as any, {
                params: { projectId },
              });
            }
          },
        });
      }
    }

    return items;
  };

  useEffect(() => {
    const fetchBreadcrumbData = async () => {
      let items: BreadcrumbItem[] = [];
      const pathSegments = location.pathname.split('/').filter(Boolean);

      // Start with static breadcrumb (dashboard navigation)
      items = buildStaticBreadcrumb();

      // Handle settings routes
      if (pathSegments[0] === 'settings' && pathSegments.length > 1) {
        items.push({
          id: 'settings',
          name: 'Settings',
          isLast: false,
          navigateTo: () => settingsNav.navigateTo('PERSONAL'),
        });

        const settingsPage = pathSegments[1];
        const pageName = SETTINGS_PAGE_NAMES[settingsPage] || settingsPage;

        items.push({
          id: settingsPage,
          name: pageName,
          isLast: true,
          navigateTo: () => {},
        });
      }
      // Handle system-admin routes
      else if (pathSegments[0] === 'system-admin') {
        items.push({
          id: 'system-admin',
          name: 'System Admin',
          isLast: false,
          navigateTo: () => settingsNav.navigateTo('SYSTEM_ADMIN'),
        });

        if (pathSegments.length > 1) {
          const systemAdminPage = pathSegments[1];
          const pageName =
            SYSTEM_ADMIN_PAGE_NAMES[systemAdminPage] || systemAdminPage;

          items.push({
            id: systemAdminPage,
            name: pageName,
            isLast: true,
            navigateTo: () => {},
          });
        } else {
          // Just system-admin without subpage
          items[items.length - 1].isLast = true;
        }
      }
      // Handle org-admin routes
      else if (pathSegments[0] === 'org-admin') {
        const organizationId = pathSegments[1] ?? '';
        items.push({
          id: 'org-admin',
          name: 'Organization Admin',
          isLast: false,
          navigateTo: () =>
            orgAdminNav.navigateTo('DASHBOARD', {
              params: { organizationId },
            }),
        });

        if (pathSegments.length > 2) {
          const adminPage = pathSegments[2];
          const pageName = ADMIN_PAGE_NAMES[adminPage] || adminPage;

          items.push({
            id: adminPage,
            name: pageName,
            isLast: true,
            navigateTo: () => {},
          });
        } else {
          // Just org-admin without subpage
          items[items.length - 1].isLast = true;
        }
      }
      // Handle governance routes
      else if (pathSegments[0] === 'governance' && pathSegments.length > 1) {
        // Determine library type based on session storage
        const sessionLibraryType = sessionStorage.getItem(
          'governanceLibraryType'
        );

        const isAssuranceLibrary = sessionLibraryType === 'assurance';

        // Add appropriate library navigation
        items.push({
          id: isAssuranceLibrary ? 'assurance-library' : 'policies',
          name: isAssuranceLibrary ? 'Assurance Library' : 'Policies',
          isLast: false,
          navigateTo: () =>
            dashboard.navigateTo(
              isAssuranceLibrary ? 'ASSURANCE_LIBRARY' : 'POLICY_LIBRARY'
            ),
        });

        // Handle governance page
        if (pathSegments.length > 2) {
          const governancePage = pathSegments[2];
          const pageName =
            GOVERNANCE_PAGE_NAMES[governancePage] || governancePage;

          if (pageName) {
            items.push({
              id: governancePage,
              name: pageName,
              isLast: true,
              navigateTo: () => {},
            });
          }
        }
      }
      // Handle project-specific routes
      else if (
        projectId &&
        (pathSegments[0] === 'project' || pathSegments[0] === 'ai-system')
      ) {
        // Build the synchronously-known portion of the breadcrumb (project +
        // page + any nested model/candidate/agent slot) with placeholder
        // names. Render it immediately so the bar is visible before any
        // async query resolves, and so a query failure can't strand it
        // empty — the names are upgraded in place once data arrives.
        items.push({
          id: `project-${projectId}`,
          name: contextProjectNameForRoute || 'AI System',
          isLast: false,
          navigateTo: () =>
            aiSystem.navigateTo('DASHBOARD', {
              params: { projectId },
            }),
        });
        items = buildProjectPageBreadcrumb(items, pathSegments);

        if (functionalModelId) {
          items.push({
            id: functionalModelId,
            name: 'AI Use Case',
            isLast: !candidateId && !agentId,
            navigateTo: () =>
              functionalModel.navigateTo('DASHBOARD', {
                params: { projectId, functionalModelId },
              }),
          });

          if (candidateId) {
            items.push({
              id: candidateId,
              name: 'Candidate Model',
              isLast: true,
              navigateTo: () =>
                candidateModel.navigateTo('CANDIDATE_DETAILS', {
                  params: { projectId, functionalModelId, candidateId },
                }),
            });
          }

          if (agentId) {
            items.push({
              id: agentId,
              name: 'Candidate Agent',
              isLast: true,
              navigateTo: () =>
                candidateAgent.navigateTo('AGENT_DETAILS', {
                  params: { projectId, functionalModelId, agentId },
                }),
            });
          }
        }

        setBreadcrumbItems([...items]);

        // Upgrade placeholder names in place. A failure on any single
        // query leaves that slot showing its placeholder rather than
        // collapsing the whole breadcrumb.
        const upgradeName = (id: string, name: string | undefined | null) => {
          if (!name) {
            return;
          }
          const idx = items.findIndex((it) => it.id === id);
          if (idx >= 0) {
            items[idx] = { ...items[idx], name };
          }
        };

        try {
          // Only fetch the outermost project if the dashboard context has
          // settled (loading=false) and still doesn't have its name. While
          // the context is still loading we wait — firing here would race
          // Phase 2 and produce a duplicate `ProjectFromId` request.
          if (!contextProjectNameForRoute && !dashboardLoading) {
            const aiSystemResult = await getProject({
              variables: { id: projectId, dashboard: true },
            });
            upgradeName(
              `project-${projectId}`,
              aiSystemResult.data?.projectFromId?.name
            );
          }

          if (functionalModelId) {
            const modelResult = await getProject({
              variables: { id: functionalModelId, dashboard: true },
            });
            upgradeName(
              functionalModelId,
              modelResult.data?.projectFromId?.name
            );

            if (candidateId) {
              const candidateResult = await getProject({
                variables: { id: candidateId, dashboard: true },
              });
              upgradeName(
                candidateId,
                candidateResult.data?.projectFromId?.name
              );
            }

            if (agentId) {
              const agentResult = await getProject({
                variables: { id: agentId, dashboard: true },
              });
              upgradeName(agentId, agentResult.data?.projectFromId?.name);
            }
          }
        } catch (error) {
          logger.error({ err: error }, 'Error fetching breadcrumb data');
        }
      } else {
        // For dashboard-only pages, update the last item to be marked as last
        if (items.length > 0) {
          items[items.length - 1].isLast = true;
        }
      }

      setBreadcrumbItems(items);
    };
    fetchBreadcrumbData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Route utility functions (dashboard, aiSystem, project, etc.) are stable and don't need to be in dependencies
  }, [
    projectId,
    functionalModelId,
    candidateId,
    agentId,
    location.pathname,
    contextProjectNameForRoute,
    dashboardLoading,
  ]);

  if (breadcrumbItems.length === 0) {
    return null;
  }

  if (compact) {
    const lastItem = breadcrumbItems[breadcrumbItems.length - 1];
    const secondLastItem =
      breadcrumbItems.length > 1
        ? breadcrumbItems[breadcrumbItems.length - 2]
        : null;

    const navTokens = inTopBar
      ? getDesignTokens(theme.palette.mode === 'dark' ? 'dark' : 'light').nav
      : null;
    const textColor = navTokens?.itemTextOnDark ?? theme.palette.text.primary;
    const secondaryColor =
      navTokens?.itemTextOnDarkMuted ?? theme.palette.text.secondary;
    const hoverColor =
      navTokens?.itemTextOnDarkHover ?? theme.palette.primary.main;
    const disabledColor = navTokens
      ? 'rgba(255,255,255,0.5)'
      : theme.palette.text.disabled;

    const hasHiddenAncestors = breadcrumbItems.length > 2;

    return (
      <Box
        sx={{
          color: textColor,
          fontWeight: 600,
          fontSize: '0.875rem',
          display: 'flex',
          alignItems: 'center',
          gap: 0.25,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {secondLastItem && (
          <>
            {hasHiddenAncestors && (
              <>
                <span
                  style={{
                    color: disabledColor,
                    fontSize: '12px',
                    flexShrink: 0,
                    lineHeight: 1,
                  }}
                >
                  …
                </span>
                <ChevronRightIcon
                  sx={{ fontSize: 14, color: disabledColor, flexShrink: 0 }}
                />
              </>
            )}
            <Link
              component="button"
              underline="none"
              onClick={secondLastItem.navigateTo}
              sx={{
                cursor: 'pointer',
                color: secondaryColor,
                flexShrink: 1,
                minWidth: '32px',
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'inline-block',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                lineHeight: 1.4,
                background: 'none',
                border: 'none',
                padding: '4px 2px',
                touchAction: 'manipulation',
                '&:hover': { color: hoverColor },
              }}
            >
              {secondLastItem.name}
            </Link>
            <ChevronRightIcon
              sx={{ fontSize: 14, color: disabledColor, flexShrink: 0 }}
            />
          </>
        )}
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: '1 1 auto',
          }}
        >
          {lastItem?.name}
        </span>
        {projectId && (
          <IconButton
            size="small"
            aria-label="Project settings"
            onClick={(e) => {
              e.stopPropagation();
              aiSystem.navigateTo('SETTINGS', { params: { projectId } });
            }}
            sx={{
              p: 0.25,
              ml: 0.5,
              flexShrink: 0,
              color: 'inherit',
              opacity: 0.6,
              '&:hover': { opacity: 1 },
            }}
          >
            <SettingsIcon sx={{ fontSize: 16 }} />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <>
      <Card
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: `${DesignToken.radiusSmall}px`,
          padding: `${spc.s}px ${spc.m}px`,
          marginBottom: `${spc.m}px`,
          boxShadow: theme.shadows[1],
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
          transition: 'box-shadow 0.2s ease-in-out',
        }}
      >
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={
            <ChevronRightIcon
              sx={{
                color: theme.palette.divider,
                fontSize: '18px',
                transition: 'color 0.2s ease-in-out',
              }}
            />
          }
          sx={{
            '& .MuiBreadcrumbs-li': {
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiBreadcrumbs-separator': {
              margin: `0 ${spc.xs}px`,
            },
          }}
        >
          {breadcrumbItems.map((item) =>
            item.isLast ? (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spc.xs,
                  backgroundColor: theme.palette.action.hover,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: `${DesignToken.radiusSmall}px`,
                  padding: `${spc.xs}px ${spc.s}px`,
                  minHeight: '32px',
                }}
              >
                {item.icon && (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: theme.palette.primary.main,
                      fontSize: '16px',
                    }}
                  >
                    {item.icon}
                  </span>
                )}
                <Text
                  sz="body16"
                  sx={{
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    fontSize: '14px',
                    lineHeight: '20px',
                  }}
                >
                  {item.name}
                </Text>
              </div>
            ) : (
              <Link
                key={item.id}
                component="button"
                underline="none"
                color="inherit"
                onClick={item.navigateTo}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: spc.xs,
                  color: theme.palette.text.primary,
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  padding: `${spc.xs}px ${spc.s}px`,
                  borderRadius: `${DesignToken.radiusSmall}px`,
                  transition: 'all 0.2s ease-in-out',
                  minHeight: '44px',
                  background: 'none',
                  border: 'none',
                  touchAction: 'manipulation',
                  textDecoration: 'none',
                  '&:hover': {
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.action.hover,
                    textDecoration: 'none',
                    transform: 'translateY(-1px)',
                    '& .breadcrumb-icon': {
                      color: theme.palette.primary.main,
                    },
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                }}
              >
                {item.icon && (
                  <span
                    className="breadcrumb-icon"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      color: theme.palette.primary.main,
                      fontSize: '16px',
                      transition: 'color 0.2s ease-in-out',
                    }}
                  >
                    {item.icon}
                  </span>
                )}
                {item.name}
              </Link>
            )
          )}
        </Breadcrumbs>
      </Card>
      <Spacer pb={spc.xs} />
    </>
  );
};

export default DynamicBreadcrumb;
